import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles } from 'lucide-react';
import { agencyInfo } from '../data/mock';
import { trackWhatsAppClick } from '../utils/analytics';
import LogoMark from './LogoMark';

/**
 * HeroStudio — Nova abertura da COUTIN STUDIO.
 * Composição visual escura + azul elétrico + laranja de acento.
 * NÃO afirma que o espaço já está aberto. Comunica "chegando em 2027".
 */
const HeroStudio = () => {
  const scrollToWaitlist = () => {
    const el = document.getElementById('waitlist');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const openWhatsApp = () => {
    trackWhatsAppClick('hero-studio', 'Falar com a Coutin');
    window.open(
      `https://wa.me/${agencyInfo.whatsapp}?text=${encodeURIComponent(
        'Olá! Quero conversar com a COUTIN sobre um projeto de podcast, fotografia, vídeo ou conteúdo.'
      )}`,
      '_blank'
    );
  };

  return (
    <section
      id="home"
      data-testid="hero-studio"
      className="relative min-h-screen flex items-center overflow-hidden bg-[#0B0B0F] pt-24 pb-16"
    >
      {/* Backdrop layers */}
      <div className="absolute inset-0 pointer-events-none">
        {/* Diagonal blue wash */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_30%,rgba(37,64,163,0.25)_0%,rgba(0,0,0,0)_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_80%,rgba(245,158,11,0.10)_0%,rgba(0,0,0,0)_50%)]" />
        {/* Grid */}
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              'linear-gradient(rgba(37,64,163,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(37,64,163,0.5) 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
        {/* Grain */}
        <div
          className="absolute inset-0 opacity-[0.05] mix-blend-overlay"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E\")",
          }}
        />
      </div>

      <div className="container mx-auto px-6 lg:px-12 relative z-10 max-w-6xl">
        {/* Coming soon pill */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-center mb-10"
        >
          <div
            data-testid="coming-2027-badge"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#2540A3]/40 bg-[#2540A3]/10 backdrop-blur-sm"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#F59E0B]" strokeWidth={2.5} />
            <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] font-bold text-[#F5F5F5]">
              Chegando em 2027 — Foz do Iguaçu
            </span>
          </div>
        </motion.div>

        {/* Logo mark */}
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="flex justify-center mb-10"
        >
          <LogoMark className="text-6xl md:text-7xl lg:text-8xl" />
        </motion.div>

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="text-center"
        >
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-black tracking-tight text-[#F5F5F0] leading-[1.05] mb-6">
            Um novo espaço para{' '}
            <span className="text-[#2540A3]">criar</span>,{' '}
            <span className="text-[#2540A3]">produzir</span> e{' '}
            <span className="text-[#F59E0B] italic">conectar</span>.
          </h1>

          <p className="text-base md:text-lg lg:text-xl text-[#A1A1AA] max-w-2xl mx-auto font-light leading-relaxed mb-4">
            Foz do Iguaçu ganhará um novo ambiente para podcasts, fotografia,
            vídeos e experiências audiovisuais.
          </p>
          <p className="text-sm md:text-base text-[#8A8A8A] max-w-xl mx-auto font-light italic mb-10">
            Mais que um estúdio. Um lugar para ideias.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <motion.button
              onClick={scrollToWaitlist}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              data-testid="hero-cta-waitlist"
              className="group inline-flex items-center gap-2 bg-[#2540A3] hover:bg-[#1A3690] text-white font-bold uppercase tracking-wide text-sm px-7 py-4 rounded-lg shadow-[0_20px_60px_-15px_rgba(37,64,163,0.6)] transition-all duration-300"
            >
              <span>Quero acompanhar o lançamento</span>
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </motion.button>

            <motion.button
              onClick={openWhatsApp}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              data-testid="hero-cta-whatsapp"
              className="inline-flex items-center gap-2 border border-white/25 hover:border-[#F59E0B] hover:bg-[#F59E0B]/5 text-[#F5F5F0] font-bold uppercase tracking-wide text-sm px-7 py-4 rounded-lg transition-all duration-300"
            >
              Falar com a Coutin
            </motion.button>
          </div>

          {/* Tag line */}
          <div className="mt-16 flex items-center justify-center gap-6 text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#8A8A8A] font-medium">
            <span>Estúdio</span>
            <span className="w-1 h-1 rounded-full bg-[#2540A3]" />
            <span>Podcast</span>
            <span className="w-1 h-1 rounded-full bg-[#2540A3]" />
            <span>Fotografia</span>
            <span className="w-1 h-1 rounded-full bg-[#2540A3]" />
            <span>Conteúdo</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroStudio;
